// Unpublished Work © 2023 Deere & Company.
import "./scss/index.scss";
import "regenerator-runtime"; // Required for async/await to work with ES5 browserlist target
import { createRoot } from "react-dom/client";
import App from "./App";
import { ConfigProvider, WindowWithAppConfig } from "./context/ConfigContext";
import reportWebVitals from "./reportWebVitals";

const EMPTY_REJECTION_MESSAGES = new Set([
  "",
  "empty reason",
  "undefined",
  "null",
]);

const isEmptyReason = (reason: unknown): boolean => {
  if (typeof reason === "string") {
    return EMPTY_REJECTION_MESSAGES.has(reason.trim().toLowerCase());
  }
  return reason === undefined || reason === null;
};

const appConfig = (window as WindowWithAppConfig).APP_CONFIG;
const enableUnhandledRejectionGuard = Boolean(
  appConfig?.ENABLE_DATADOG_UNHANDLED_REJECTION_GUARD
);
const isProdEnvironment = appConfig?.NODE_ENV === "prod";

if (enableUnhandledRejectionGuard) {
  window.addEventListener("unhandledrejection", (event) => {
    const { reason } = event;
    const normalizedReason =
      reason instanceof Error
        ? reason
        : new Error(
            `Unhandled promise rejection: ${
              reason === undefined || reason === null
                ? "empty reason"
                : String(reason)
            }`
          );

    if (!isProdEnvironment) {
      console.warn("[UnhandledRejection]", {
        pathname: window.location.pathname,
        reason,
        normalizedReason,
      });
    }

    // Avoid noisy 'Uncaught (in promise) Empty reason' browser events.
    if (isEmptyReason(reason)) {
      event.preventDefault();
    }
  });
}

const root = createRoot(document.getElementById("root")!);
root.render(
  <ConfigProvider>
    <App />
  </ConfigProvider>
);

if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/service-worker.js");
  });
}

reportWebVitals(console.log);
